import { apiClient, toQueryParams } from './client';
import type { Brand, PagedResponse, PaginationParams } from '@/types';

/** Raw shape returned by the backend (BrandDto). */
interface BrandDto {
  brandId: number;
  name: string | null;
  industry: string | null;
  industrySegment: string | null;
  logoUrl: string | null;
  isActive: boolean;
  createdDate: string;
}

interface BrandDtoPagedResult {
  items: BrandDto[] | null;
  page: number;
  pageSize: number;
  totalCount: number;
  totalPages: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
}

function toBrand(dto: BrandDto): Brand {
  return {
    id: dto.brandId,
    name: dto.name ?? '',
    industry: dto.industry ?? undefined,
    industrySegment: dto.industrySegment ?? undefined,
    logoUrl: dto.logoUrl ?? undefined,
    isActive: dto.isActive,
    createdAt: dto.createdDate,
  };
}

export interface BrandInput {
  name: string;
  industry?: string;
  industrySegment?: string;
  logoUrl?: string;
  isActive?: boolean;
}

export const brandsApi = {
  list: async (params: PaginationParams = {}): Promise<PagedResponse<Brand>> => {
    const { data } = await apiClient.get<BrandDtoPagedResult>('/brands', {
      params: toQueryParams({ page: 1, pageSize: 50, ...params }),
    });
    return {
      items: (data.items ?? []).map(toBrand),
      page: data.page,
      pageSize: data.pageSize,
      totalCount: data.totalCount,
      totalPages: data.totalPages,
    };
  },
  getById: async (id: number): Promise<Brand> => {
    const { data } = await apiClient.get<BrandDto>(`/brands/${id}`);
    return toBrand(data);
  },
  create: async (input: BrandInput): Promise<Brand> => {
    const { data } = await apiClient.post<BrandDto>('/brands', {
      name: input.name,
      industry: input.industry,
      industrySegment: input.industrySegment,
      logoUrl: input.logoUrl,
    });
    return toBrand(data);
  },
  update: async (id: number, input: BrandInput): Promise<Brand> => {
    const { data } = await apiClient.put<BrandDto>(`/brands/${id}`, {
      name: input.name,
      industry: input.industry,
      industrySegment: input.industrySegment,
      logoUrl: input.logoUrl,
      isActive: input.isActive,
    });
    return toBrand(data);
  },
  remove: async (id: number): Promise<void> => {
    await apiClient.delete(`/brands/${id}`);
  },
};

/** Competitor row returned by `/brands/{id}/competitors` (BrandCompetitorDto). */
export interface BrandCompetitor {
  competitorBrandId: number;
  name: string;
  industry: string | null;
  logoUrl: string | null;
  isActive: boolean;
  /** This brand's preferred default for the Comparison page (at most one per brand). */
  isDefault: boolean;
}

/**
 * Per-brand competitor set. Reads are open to any authenticated user with
 * access to the brand; writes are Admin-only on the backend.
 */
export const brandCompetitorsApi = {
  /** GET /brands/{brandId}/competitors — full competitor list with details. */
  list: async (brandId: number): Promise<BrandCompetitor[]> => {
    const { data } = await apiClient.get<BrandCompetitor[] | null>(
      `/brands/${brandId}/competitors`,
    );
    return data ?? [];
  },

  /** GET /brands/{brandId}/competitors/ids — lightweight id-only list. */
  listIds: async (brandId: number): Promise<number[]> => {
    const { data } = await apiClient.get<number[] | null>(`/brands/${brandId}/competitors/ids`);
    return data ?? [];
  },

  /** PUT /brands/{brandId}/competitors — replace the full competitor set (Admin only). */
  set: async (
    brandId: number,
    competitorBrandIds: number[],
    defaultCompetitorBrandId?: number,
  ): Promise<BrandCompetitor[]> => {
    const { data } = await apiClient.put<BrandCompetitor[] | null>(
      `/brands/${brandId}/competitors`,
      { competitorBrandIds, defaultCompetitorBrandId: defaultCompetitorBrandId ?? null },
    );
    return data ?? [];
  },

  /** POST /brands/{brandId}/competitors/{competitorBrandId} — add a single competitor (Admin only). */
  add: async (brandId: number, competitorBrandId: number): Promise<void> => {
    await apiClient.post(`/brands/${brandId}/competitors/${competitorBrandId}`);
  },

  /** DELETE /brands/{brandId}/competitors/{competitorBrandId} — remove a single competitor (Admin only). */
  remove: async (brandId: number, competitorBrandId: number): Promise<void> => {
    await apiClient.delete(`/brands/${brandId}/competitors/${competitorBrandId}`);
  },
};
